import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Shield, TrendingUp, Building2, MapPin, Zap, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_CONFIG = {
  institutional: { label: "Institutional", color: "text-emerald-700", bg: "bg-emerald-100 border-emerald-300", bar: "bg-emerald-500" },
  strong: { label: "Strong", color: "text-blue-700", bg: "bg-blue-100 border-blue-300", bar: "bg-blue-500" },
  moderate: { label: "Moderate", color: "text-amber-700", bg: "bg-amber-100 border-amber-300", bar: "bg-amber-500" },
  elevated: { label: "Elevated", color: "text-red-700", bg: "bg-red-100 border-red-300", bar: "bg-red-500" },
};

function ScoreBar({ label, score, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`h-3.5 w-3.5 ${color} shrink-0`} />
      <span className="text-[10px] text-[#666] w-20 shrink-0">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, background: score >= 70 ? "#059669" : score >= 50 ? "#d97706" : "#dc2626" }} />
      </div>
      <span className="text-[10px] font-bold text-[#333] w-6 text-right">{score}</span>
    </div>
  );
}

export function DealRiskScoreCard({ leadId, compact = false }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    axios.get(`${API}/scoring/deals/${leadId}/risk-score`)
      .then(r => setScore(r.data))
      .catch(() => setScore(null))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="flex items-center gap-1 py-1"><Loader2 className="h-3 w-3 animate-spin text-emerald-600" /><span className="text-[9px] text-[#999]">Scoring...</span></div>;
  if (!score) return null;

  const band = BAND_CONFIG[score.risk_band] || BAND_CONFIG.moderate;

  if (compact) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`risk-score-compact-${leadId}`}>
        <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${band.bg}`}>
          <Shield className="h-2.5 w-2.5 inline mr-0.5" />{score.overall_score}
        </div>
        <span className={`text-[8px] font-bold ${band.color}`}>{band.label}</span>
        {score.confidence_score && <span className="text-[8px] text-[#bbb]">{score.confidence_score}% conf.</span>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid={`risk-score-card-${leadId}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold text-[#333] flex items-center gap-1.5">
          <Shield className="h-4 w-4 text-emerald-600" /> Deal Risk Score
        </h4>
        <button onClick={() => setExpanded(!expanded)} className="text-[#999] hover:text-[#333]" data-testid="risk-score-expand">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="flex items-center gap-4 mb-3">
        <div className="text-center">
          <div className={`text-3xl font-black ${band.color}`}>{score.overall_score}</div>
          <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold border mt-1 ${band.bg}`}>{band.label}</div>
        </div>
        <div className="flex-1 space-y-1.5">
          <ScoreBar label="Borrower" score={score.borrower_strength_score} icon={Building2} color="text-blue-500" />
          <ScoreBar label="Deal" score={score.deal_quality_score} icon={TrendingUp} color="text-emerald-500" />
          <ScoreBar label="Market" score={score.market_strength_score} icon={MapPin} color="text-purple-500" />
          <ScoreBar label="Execution" score={score.execution_risk_score} icon={Zap} color="text-amber-500" />
        </div>
      </div>

      {score.confidence_score != null && (
        <div className="flex items-center gap-2 text-[10px] text-[#888] mb-2">
          <span>Confidence:</span>
          <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-cyan-500" style={{ width: `${score.confidence_score}%` }} />
          </div>
          <span className="font-bold">{score.confidence_score}%</span>
        </div>
      )}

      {expanded && score.score_explanations && (
        <div className="mt-3 pt-3 border-t border-[#f0ebe0] space-y-2" data-testid="risk-score-details">
          {Object.entries(score.score_explanations).map(([category, reasons]) => (
            <div key={category}>
              <div className="text-[9px] font-bold text-[#999] uppercase mb-0.5">{category.replace(/_/g, " ")}</div>
              {(reasons || []).map((r, i) => (
                <div key={i} className="text-[10px] text-[#555] pl-2 border-l-2 border-[#e8e0d0] mb-0.5">{r}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function LenderFitScoreBadge({ leadId, lenderUserId, fitScore }) {
  const band = fitScore >= 80 ? BAND_CONFIG.institutional : fitScore >= 60 ? BAND_CONFIG.strong : fitScore >= 40 ? BAND_CONFIG.moderate : BAND_CONFIG.elevated;
  return (
    <div className="flex items-center gap-1.5" data-testid={`fit-score-${leadId}-${lenderUserId}`}>
      <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${band.bg}`}>
        <TrendingUp className="h-2.5 w-2.5 inline mr-0.5" />{fitScore}
      </div>
      <span className={`text-[8px] font-bold ${band.color}`}>Fit</span>
    </div>
  );
}

export function MyLenderFitBadge({ leadId }) {
  const [fitScore, setFitScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) return;
    axios.get(`${API}/scoring/deals/${leadId}/lender-fit`)
      .then(r => {
        const scores = r.data?.scores || [];
        // The current user's score will be matched by the backend auth
        // For now, show the average/top score as a "match quality" indicator
        if (scores.length > 0) {
          setFitScore(scores[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading || !fitScore) return null;

  const score = fitScore.fit_score;
  const band = score >= 80 ? BAND_CONFIG.institutional : score >= 60 ? BAND_CONFIG.strong : score >= 40 ? BAND_CONFIG.moderate : BAND_CONFIG.elevated;

  return (
    <div className="flex items-center gap-1.5" data-testid={`my-fit-score-${leadId}`}>
      <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${band.bg}`}>
        <TrendingUp className="h-2.5 w-2.5 inline mr-0.5" />{score}
      </div>
      <span className={`text-[8px] font-bold ${band.color}`}>Your Fit</span>
    </div>
  );
}

export function OpportunityScoreCard({ opportunityId, compact = false }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!opportunityId) return;
    setLoading(true);
    axios.get(`${API}/scoring/opportunities/${opportunityId}/opportunity-score`)
      .then(r => setScore(r.data))
      .catch(() => setScore(null))
      .finally(() => setLoading(false));
  }, [opportunityId]);

  if (loading) return <div className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin text-emerald-600" /></div>;
  if (!score) return null;

  const overall = score.overall_score;
  const color = overall >= 70 ? "text-emerald-700" : overall >= 50 ? "text-amber-700" : "text-red-700";

  if (compact) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`opp-score-compact-${opportunityId}`}
        title="Imaani Underwriting Score (0-100)">
        <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold border cursor-help ${overall >= 70 ? "bg-emerald-100 border-emerald-300 text-emerald-700" : overall >= 50 ? "bg-amber-100 border-amber-300 text-amber-700" : "bg-red-100 border-red-300 text-red-700"}`}>
          Score: {overall}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white p-4" data-testid={`opp-score-card-${opportunityId}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold text-[#333] flex items-center gap-1.5">
          <Shield className="h-4 w-4 text-indigo-600" /> Opportunity Score
        </h4>
        <span className={`text-2xl font-black ${color}`}>{overall}</span>
      </div>
      <div className="space-y-1.5">
        <ScoreBar label="Sponsor" score={score.sponsor_strength_score} icon={Building2} color="text-blue-500" />
        <ScoreBar label="Downside" score={score.downside_protection_score} icon={Shield} color="text-emerald-500" />
        <ScoreBar label="Structure" score={score.structure_quality_score} icon={TrendingUp} color="text-purple-500" />
        <ScoreBar label="Yield" score={score.yield_stability_score} icon={Zap} color="text-amber-500" />
      </div>
      {score.rationale?.reasons?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-[#f0ebe0]">
          {score.rationale.reasons.slice(0, 4).map((r, i) => (
            <div key={i} className="text-[10px] text-[#555] pl-2 border-l-2 border-indigo-200 mb-0.5">{r}</div>
          ))}
        </div>
      )}
    </div>
  );
}
